// Error handling utility for production environment
import { message } from 'antd';

// Global error handler for API calls
export const handleApiError = (error, customMessage = null) => {
    console.error('API Error:', error);
    
    let errorMessage = customMessage;
    
    if (!errorMessage) {
        if (error.response) {
            // Server responded with error status
            const status = error.response.status;
            const data = error.response.data;
            
            switch (status) {
                case 400:
                    errorMessage = data?.message || 'Invalid request. Please check your input.';
                    break;
                case 401:
                    errorMessage = 'Authentication required. Please log in again.';
                    break;
                case 403:
                    errorMessage = 'Access denied. You don\'t have permission for this action.';
                    break;
                case 404:
                    errorMessage = 'Resource not found.';
                    break;
                case 422:
                    errorMessage = data?.message || 'Validation error. Please check your input.';
                    break;
                case 500:
                    errorMessage = 'Server error. Please try again later.';
                    break;
                default:
                    errorMessage = data?.message || 'An unexpected error occurred.';
            }
        } else if (error.request) {
            // Network error
            errorMessage = 'Network error. Please check your internet connection.';
        } else {
            // Other errors
            errorMessage = error.message || 'An unexpected error occurred.';
        }
    }
    
    message.error(errorMessage);
    return errorMessage;
};

// Export function error handler
export const handleExportError = (error, exportType = 'file') => {
    console.error(`${exportType.toUpperCase()} Export Error:`, error);
    
    let errorMessage = `Failed to generate ${exportType}. `;
    
    if (error.message.includes('library')) {
        errorMessage += 'Please refresh the page and try again.';
    } else if (error.message.includes('data')) {
        errorMessage += 'No data available for export.';
    } else {
        errorMessage += 'Please try again or contact support if the issue persists.';
    }
    
    message.error(errorMessage);
    return errorMessage;
};

// Validation error handler
export const handleValidationError = (errors) => {
    console.error('Validation Errors:', errors);
    
    if (Array.isArray(errors)) {
        errors.forEach(error => {
            message.error(error);
        });
    } else if (typeof errors === 'object') {
        Object.values(errors).forEach(error => {
            if (error) message.error(error);
        });
    } else {
        message.error(errors || 'Validation failed. Please check your input.');
    }
};

// Success message handler
export const showSuccessMessage = (messageText, duration = 3) => {
    message.success(messageText, duration);
};

// Warning message handler
export const showWarningMessage = (messageText, duration = 5) => {
    message.warning(messageText, duration);
};

// Info message handler
export const showInfoMessage = (messageText, duration = 4) => {
    message.info(messageText, duration);
};

// Log error for monitoring (in production, this could send to error tracking service)
export const logError = (error, context = '') => {
    const errorLog = {
        timestamp: new Date().toISOString(),
        context,
        error: {
            message: error.message,
            stack: error.stack,
            name: error.name
        },
        userAgent: navigator.userAgent,
        url: window.location.href
    };
    
    console.error('Error Log:', errorLog);
    
    // In production, you could send this to your error tracking service
    // Example: Sentry.captureException(error, { extra: errorLog });
    
    return errorLog;
}; 